import type { MatterColumn } from "hooks/useEmailsRestApi";
import type React from "react";
import type { FC } from "react";

type ParsedMatterSummaryViewProps = {
  matterColumn: MatterColumn;
};

// 単価をフォーマット（万円単位で表示）
const formatReward = (reward: string | null | undefined): string => {
  if (!reward || reward === "null") return "-";
  const num = parseInt(reward, 10);
  if (Number.isNaN(num)) return reward;
  // 値が1000以上の場合は万円単位に変換（例: 800000 → 80万円）
  if (num >= 1000) {
    return `${Math.round(num / 10000)}万円`;
  }
  return `${num}万円`;
};

// リモート頻度をフォーマット
const formatRemoteFrequency = (
  frequency: string | null | undefined,
): string => {
  if (!frequency) return "-";
  switch (frequency) {
    case "full_remote":
      return "フルリモート";
    case "hybrid":
      return "ハイブリッド";
    case "onsite_only":
      return "常駐";
    default:
      return frequency;
  }
};

// 面談方法をフォーマット
const formatInterviewMethod = (method: string | null | undefined): string => {
  if (!method) return "-";
  switch (method) {
    case "web":
      return "Web面談";
    case "onsite":
      return "対面";
    case "combined":
      return "Web + 対面";
    default:
      return method;
  }
};

// Boolean値をフォーマット
const formatBoolean = (
  value: boolean | null | undefined,
  trueLabel = "可",
  falseLabel = "不可",
): string => {
  if (value === null || value === undefined) return "-";
  return value ? trueLabel : falseLabel;
};

// 配列をカンマ区切りで表示
const formatArray = (arr: string[] | null | undefined): string => {
  if (!arr || arr.length === 0) return "-";
  return arr.join("、");
};

// 文字列をフォーマット
const formatString = (
  value: string | null | undefined,
  suffix = "",
): string => {
  if (value === null || value === undefined || value === "" || value === "null")
    return "-";
  return value + suffix;
};

// 数値をフォーマット
const formatNumber = (
  value: number | string | null | undefined,
  suffix = "",
): string => {
  if (value === null || value === undefined || value === "null") return "-";
  if (typeof value === "string") {
    if (value === "") return "-";
    return value + suffix;
  }
  return value + suffix;
};

// 値が空かどうかをチェック
const isEmpty = (value: string): boolean => value === "-";

// 行コンポーネント
type RowProps = {
  label: string;
  value: string;
  children?: React.ReactNode;
};

const Row: FC<RowProps> = ({ label, value, children }) => {
  const isEmptyValue = isEmpty(value);
  const labelClass = isEmptyValue
    ? "font-semibold text-gray-400"
    : "font-semibold text-gray-700";
  const valueClass = isEmptyValue ? "text-gray-400" : "";

  return (
    <div className={isEmptyValue ? "text-gray-400" : ""}>
      <span className={labelClass}>■ {label}：</span>
      {children ? children : <span className={valueClass}>{value}</span>}
    </div>
  );
};

// サマリテキストを生成する関数（コピー用）
export const generateMatterSummaryText = (
  matterColumn: MatterColumn,
): string => {
  const detail = formatString(matterColumn.detail);
  const devEnv = formatArray(matterColumn.developmentEnvironment ?? undefined);
  const requiredSkills = formatArray(matterColumn.requiredSkills ?? undefined);
  const niceToHaveSkills = formatArray(
    matterColumn.niceToHaveSkills ?? undefined,
  );
  const period = formatString(matterColumn.period);
  const interviewMethod = formatInterviewMethod(matterColumn.interviewMethod);
  const numberOfInterviews = formatNumber(
    matterColumn.numberOfInterviews,
    "回",
  );
  const reward = formatReward(matterColumn.reward);
  const remoteFrequency = formatRemoteFrequency(matterColumn.remoteFrequency);
  const station = formatString(matterColumn.station);
  const workingHours = formatString(matterColumn.workingHours);
  const reqcuitmentRequired = formatNumber(
    matterColumn.reqcuitmentRequired,
    "名",
  );
  const workingTimeWidth = formatString(matterColumn.workingTimeWidth);
  const paymentSite = formatNumber(matterColumn.paymentSite, "日");
  const otherCountries = formatBoolean(matterColumn.otherCountriesEnabled);
  const countryside = formatBoolean(matterColumn.isCountrysideEnabled);
  const remarks = formatString(matterColumn.remarks);

  const interviewValue = `面談方法：${interviewMethod}、面談回数：${numberOfInterviews}`;

  const lines = [
    `■ 案件詳細：`,
    `    ${detail}`,
    ``,
    `■ 開発環境：${devEnv}`,
    `■ 必須スキル：${requiredSkills}`,
    `■ 尚可スキル：${niceToHaveSkills}`,
    ``,
    `■ 開始時期：${period}`,
    `■ 面談詳細事項：${interviewValue}`,
    `■ 単価：${reward}`,
    `■ 就業方法：${remoteFrequency}`,
    `■ 最寄駅：${station}`,
    `■ 就業時間：${workingHours}`,
    `■ 募集人数：${reqcuitmentRequired}`,
    `■ 精算幅：${workingTimeWidth}`,
    `■ 支払いサイト：${paymentSite}`,
    `■ 外国籍：${otherCountries}`,
    `■ 地方：${countryside}`,
    ``,
    `■ 備考：`,
    `    ${remarks}`,
  ];

  return lines.join("\n");
};

export const ParsedMatterSummaryView: FC<ParsedMatterSummaryViewProps> = ({
  matterColumn,
}) => {
  const detail = formatString(matterColumn.detail);
  const devEnv = formatArray(matterColumn.developmentEnvironment ?? undefined);
  const requiredSkills = formatArray(matterColumn.requiredSkills ?? undefined);
  const niceToHaveSkills = formatArray(
    matterColumn.niceToHaveSkills ?? undefined,
  );
  const period = formatString(matterColumn.period);
  const interviewMethod = formatInterviewMethod(matterColumn.interviewMethod);
  const numberOfInterviews = formatNumber(
    matterColumn.numberOfInterviews,
    "回",
  );
  const reward = formatReward(matterColumn.reward);
  const remoteFrequency = formatRemoteFrequency(matterColumn.remoteFrequency);
  const station = formatString(matterColumn.station);
  const workingHours = formatString(matterColumn.workingHours);
  const reqcuitmentRequired = formatNumber(
    matterColumn.reqcuitmentRequired,
    "名",
  );
  const workingTimeWidth = formatString(matterColumn.workingTimeWidth);
  const paymentSite = formatNumber(matterColumn.paymentSite, "日");
  const otherCountries = formatBoolean(matterColumn.otherCountriesEnabled);
  const countryside = formatBoolean(matterColumn.isCountrysideEnabled);
  const remarks = formatString(matterColumn.remarks);

  // 面談詳細が両方空かチェック
  const isInterviewEmpty =
    isEmpty(interviewMethod) && isEmpty(numberOfInterviews);
  const interviewValue = `面談方法：${interviewMethod}、面談回数：${numberOfInterviews}`;

  return (
    <div className="text-sm text-gray-800 leading-relaxed space-y-1">
      {/* 案件詳細 */}
      <div className={`mb-4 ${isEmpty(detail) ? "text-gray-400" : ""}`}>
        <span
          className={`font-semibold ${isEmpty(detail) ? "text-gray-400" : "text-gray-700"}`}
        >
          ■ 案件詳細：
        </span>
        <div
          className={`whitespace-pre-line mt-1 pl-4 ${isEmpty(detail) ? "text-gray-400" : "text-gray-700"}`}
        >
          {detail}
        </div>
      </div>

      {/* 開発環境 */}
      <Row label="開発環境" value={devEnv} />

      {/* 必須スキル */}
      <Row label="必須スキル" value={requiredSkills} />

      {/* 尚可スキル */}
      <Row label="尚可スキル" value={niceToHaveSkills} />

      {/* 区切り */}
      <div className="my-2" />

      {/* 開始時期 */}
      <Row label="開始時期" value={period} />

      {/* 面談詳細事項 */}
      <div className={isInterviewEmpty ? "text-gray-400" : ""}>
        <span
          className={`font-semibold ${isInterviewEmpty ? "text-gray-400" : "text-gray-700"}`}
        >
          ■ 面談詳細事項：
        </span>
        <span className="ml-2">{interviewValue}</span>
      </div>

      {/* 単価 */}
      <Row label="単価" value={reward}>
        <span
          className={`font-medium ${isEmpty(reward) ? "text-gray-400" : "text-blue-600"}`}
        >
          {reward}
        </span>
      </Row>

      {/* 就業方法（リモート頻度） */}
      <Row label="就業方法" value={remoteFrequency} />

      {/* 最寄駅 */}
      <Row label="最寄駅" value={station} />

      {/* 就業時間 */}
      <Row label="就業時間" value={workingHours} />

      {/* 募集人数 */}
      <Row label="募集人数" value={reqcuitmentRequired} />

      {/* 精算幅 */}
      <Row label="精算幅" value={workingTimeWidth} />

      {/* 支払いサイト */}
      <Row label="支払いサイト" value={paymentSite} />

      {/* 外国籍 */}
      <Row label="外国籍" value={otherCountries} />

      {/* 地方 */}
      <Row label="地方" value={countryside} />

      {/* 備考 */}
      <div
        className={`mt-3 pt-2 border-t border-gray-200 ${isEmpty(remarks) ? "text-gray-400" : ""}`}
      >
        <span
          className={`font-semibold ${isEmpty(remarks) ? "text-gray-400" : "text-gray-700"}`}
        >
          ■ 備考：
        </span>
        <div
          className={`whitespace-pre-line mt-1 pl-4 ${isEmpty(remarks) ? "text-gray-400" : "text-gray-600"}`}
        >
          {remarks}
        </div>
      </div>
    </div>
  );
};
